import React, { createContext, useContext, useMemo, useState } from "react";
import { config } from "../../../../config";
import { ICP, ResearchState } from "../../../../interfaces/ICP";
import { useSelector } from "react-redux";
import ICPService from "../../../../services/ICP";
import { InitialSearchStates } from "../../../../components/SearchAndFilters/searchStates";

interface ICPContextType {
  icps: ICP[];
  loading: boolean;
  error: string | null;
  researchStatus:
    | {
        status: ResearchState;
        progress?: number;
      }
    | null
    | any;
  fetchICPs: (
    projectId: string | any,
    onUpdateSearchState: any,
    params?: {
      status?: "draft" | "active" | "archived";
      sortBy?: string;
      sortOrder?: "asc" | "desc";
      limit?: number;
      page?: number;
    }
  ) => Promise<void>;
  updateICP: (
    projectId: string | any,
    icpId: string,
    data: Partial<ICP>
  ) => Promise<ICP | null>;
  deleteICP: (projectId: string | any, icpId: string) => Promise<boolean>;
  startResearch: (
    modelAPIKey: string | any,
    projectId: string | any,
    options?: {
      socketId?: string;
      priority?: number;
      customInstructions?: string;
    }
  ) => Promise<any | false>;
}

const ICPContext = createContext<ICPContextType | null>(null);

interface ICPProviderProps {
  children: React.ReactNode;
}

export const ICPProvider: React.FC<ICPProviderProps> = ({ children }) => {
  const reduxUser = useSelector((state: any) => state.user);
  const [icps, setIcps] = useState<ICP[] | any>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [researchStatus, setResearchStatus] = useState<
    ICPContextType["researchStatus"] | any
  >(null);

  const icpService = useMemo(() => new ICPService(), []);

  const fetchICPs = async (
    projectId: string | any,
    onUpdateSearchState: any,
    params: any = InitialSearchStates.icps
  ) => {
    if (!config.inProd) {
      setIcps(config.mocks.icps);
      return;
    }

    setLoading(true);
    try {
      const status = params.filters.status;
      const sortOrder = params.sorting.sortOrder;
      const response = await icpService.getICPs(projectId, {
        userId: reduxUser.id,
        name: params.query,
        status: status && status === "all" ? undefined : status,
        sortBy: "createdAt",
        sortOrder: (sortOrder && (sortOrder === -1 ? "desc" : "asc")) || "desc",
        limit: Math.min(Math.max(1, params.pagination.limit || 20), 100),
        page: Math.max(1, params.pagination.currentPage || 1),
      });

      if (response.success && response.data) {
        // Parse stringified JSON fields if they exist
        const parsedIcps = response.data.icps.map((icp: any) => ({
          ...icp,
          targeting:
            typeof icp.targeting === "string"
              ? JSON.parse(icp.targeting)
              : icp.targeting,
          interests:
            typeof icp.interests === "string"
              ? JSON.parse(icp.interests)
              : icp.interests,
          conversion:
            typeof icp.conversion === "string"
              ? JSON.parse(icp.conversion)
              : icp.conversion,
        }));
        setIcps(parsedIcps);
        onUpdateSearchState({
          ...params,
          pagination: {
            ...params.pagination,
            ...response.data.pagination,
          },
        });
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateICP = async (
    projectId: string | any,
    icpId: string,
    data: Partial<ICP | any>
  ) => {
    if (!config.inProd) {
      const updatedICPs = icps.map((icp: ICP | any) =>
        icp._id === icpId ? { ...icp, ...data } : icp
      );
      setIcps(updatedICPs);
      return updatedICPs.find((icp: ICP | any) => icp._id === icpId) || null;
    }

    try {
      // Prepare the data by stringifying specific fields if they're objects
      const preparedData = {
        userId: reduxUser.id,
        name: data.name,
        status: data.status,
        targeting:
          data.targeting && typeof data.targeting === "object"
            ? JSON.stringify(data.targeting)
            : data.targeting,
        interests:
          data.interests && typeof data.interests === "object"
            ? JSON.stringify(data.interests)
            : data.interests,
        conversion:
          data.conversion && typeof data.conversion === "object"
            ? JSON.stringify(data.conversion)
            : data.conversion,
      };

      const response = await icpService.updateICP(
        projectId,
        icpId,
        preparedData
      );

      if (response.success && response.data) {
        const icp = response.data.icp;
        return {
          ...icp,
          targeting:
            typeof icp.targeting === "string"
              ? JSON.parse(icp.targeting)
              : icp.targeting,
          interests:
            typeof icp.interests === "string"
              ? JSON.parse(icp.interests)
              : icp.interests,
          conversion:
            typeof icp.conversion === "string"
              ? JSON.parse(icp.conversion)
              : icp.conversion,
        };
      }
      return null;
    } catch (error: any) {
      setError(error.message);
      return null;
    }
  };

  const deleteICP = async (projectId: string | any, icpId: string) => {
    if (!config.inProd) {
      setIcps((prev: any) =>
        prev.filter((icp: ICP | any) => icp._id !== icpId)
      );
      return true;
    }

    try {
      const response = await icpService.deleteICP(
        projectId,
        icpId,
        reduxUser.id
      );
      if (response.success) {
        return true;
      }
      return false;
    } catch (error: any) {
      setError(error.message);
      return false;
    }
  };

  const startResearch = async (projectId: string | any, options: any = {}) => {
    if (!config.inProd) {
      setResearchStatus({ status: "running", progress: 0 });
      return true;
    }
    const response = await icpService.startResearch(projectId, {
      userId: reduxUser.id,
      modelAPIKey: options.modelAPIKey,
      socketId: options.socketId,
      priority: 5,
      customInstructions: options.customInstructions?.slice(0, 1000),
    });
    return response.data || false;
  };

  const value: ICPContextType = {
    icps,
    loading,
    error,
    researchStatus,
    fetchICPs,
    updateICP,
    deleteICP,
    startResearch,
  };

  return <ICPContext.Provider value={value}>{children}</ICPContext.Provider>;
};

export const useICP = () => {
  const context = useContext(ICPContext);
  if (!context) {
    throw new Error("useICP must be used within an ICPProvider");
  }
  return context;
};

export default ICPProvider;
